import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { formatPrice } from "@/utils/currency";
import { getLocaleSettings } from "@/utils/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { toast } from "sonner";
import { API_URL } from "@/utils/apiConfig";
import {
  ShoppingBag, Plus, Truck, Loader2, Clock, ArrowLeft, ChefHat, Trash2
} from "lucide-react";

const sourceLabels = { uber_eats: "Uber Eats", bolt_food: "Bolt Food", whatsapp: "WhatsApp", counter: "Balcão", table: "Mesa", online: "Online", phone: "Telefono" };

function ManualProductEntry({ onAdd }) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState(1);
  const add = () => {
    if (!name || !price) return;
    onAdd({ product_id: `manual_${Date.now()}`, product_name: name, quantity: qty, unit_price: Number(price), notes: "" });
    setName(""); setPrice(""); setQty(1);
  };
  return (
    <div className="border-t pt-4 mt-4">
      <Label className="text-xs uppercase text-muted-foreground font-semibold">Agregar producto manual</Label>
      <div className="flex gap-2 mt-2">
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="Producto" className="flex-1 bg-secondary border-transparent" data-testid="manual-product-name" />
        <Input type="number" value={qty} onChange={e => setQty(Number(e.target.value))} min="1" className="w-16 bg-secondary border-transparent" />
        <Input type="number" value={price} onChange={e => setPrice(e.target.value)} step="0.01" placeholder="Precio" className="w-24 bg-secondary border-transparent" data-testid="manual-product-price" />
        <Button variant="outline" onClick={add} data-testid="add-manual-product"><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

export function VentaDirecta({ onClose, onCreated }) {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios); authAxiosRef.current = authAxios;
  const locale = getLocaleSettings();
  const [tab, setTab] = useState("mesas");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [source, setSource] = useState("counter");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [deliveryType, setDeliveryType] = useState("pickup");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryLat, setDeliveryLat] = useState(null);
  const [deliveryLng, setDeliveryLng] = useState(null);
  const [tableNumber, setTableNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [isScheduled, setIsScheduled] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [needsChange, setNeedsChange] = useState(false);
  const [cashReceived, setCashReceived] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tables, setTables] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    Promise.all([
      authAxiosRef.current.get("/menu/products"),
      authAxiosRef.current.get("/menu/categories"),
      authAxiosRef.current.get("/tables"),
      authAxiosRef.current.get("/restaurant")
    ]).then(([prodRes, catRes, tablesRes, restRes]) => {
      setProducts(prodRes.data || []);
      setCategories(catRes.data || []);
      setTables(tablesRes.data || []);
      // POS shows all active methods + methods marked pos_only (even if active=false)
      const methods = restRes.data?.payment_methods?.filter(m => m.active || m.pos_only) || [
        { name: "Efectivo", active: true },
        { name: "Visa (Debito)", active: true },
        { name: "Mastercard (Credito)", active: true }
      ];
      setPaymentMethods(methods);
      if (methods.length > 0) setPaymentMethod(methods[0].name);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const addToCart = (product) => {
    const existing = cart.find(c => c.product_id === product.id);
    if (existing) setCart(cart.map(c => c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c));
    else setCart([...cart, { product_id: product.id, product_name: product.name, quantity: 1, unit_price: product.price, category: product.category, notes: "" }]);
  };

  const updateQty = (productId, delta) => {
    setCart(cart.map(c => c.product_id === productId ? { ...c, quantity: Math.max(0, c.quantity + delta) } : c).filter(c => c.quantity > 0));
  };

  const removeItem = (productId) => setCart(cart.filter(c => c.product_id !== productId));

  const updateNotes = (productId, notes) => setCart(cart.map(c => c.product_id === productId ? { ...c, notes } : c));

  const subtotal = cart.reduce((sum, c) => sum + c.quantity * c.unit_price, 0);
  const shipping = deliveryType === "delivery" ? 2.50 : 0;
  const total = subtotal + shipping;

  const submit = async () => {
    if (!customerName.trim()) { toast.error("El nombre del cliente es obligatorio"); return; }
    if (deliveryType === "delivery" && !deliveryAddress.trim()) { toast.error("La direccion de entrega es obligatoria"); return; }
    if (cart.length === 0) { toast.error("Anade productos al pedido"); return; }
    
    // Validate cash received if needs change
    const isCashPayment = paymentMethod.toLowerCase().includes("efectivo") || paymentMethod.toLowerCase().includes("cash") || paymentMethod.toLowerCase().includes("dinheiro");
    if (isCashPayment && needsChange && (!cashReceived || parseFloat(cashReceived) < total)) {
      toast.error("Valor recebido insuficiente");
      return;
    }
    
    setSubmitting(true);
    try {
      const orderSource = deliveryType === "dineIn" ? "table" : source;
      const orderData = {
        source: orderSource,
        table_number: deliveryType === "dineIn" && tableNumber ? parseInt(tableNumber) : null,
        customer_name: customerName, customer_phone: customerPhone,
        delivery_address: deliveryType === "delivery" ? deliveryAddress : "",
        delivery_lat: deliveryType === "delivery" ? deliveryLat : null,
        delivery_lng: deliveryType === "delivery" ? deliveryLng : null,
        notes, payment_method: paymentMethod,
        scheduled_for: isScheduled && scheduledFor ? scheduledFor : "",
        items: cart.map(c => ({ product_id: c.product_id, product_name: c.product_name, quantity: c.quantity, unit_price: c.unit_price, notes: c.notes || "" }))
      };
      
      // Add cash/change info if applicable
      if (isCashPayment && needsChange && cashReceived) {
        orderData.cash_received = parseFloat(cashReceived);
        orderData.change_given = parseFloat(cashReceived) - total;
      }
      
      await authAxios.post("/orders", orderData);
      
      // Show change if applicable
      if (isCashPayment && needsChange && cashReceived && parseFloat(cashReceived) > total) {
        toast.success(`Pedido criado! Troco: €${(parseFloat(cashReceived) - total).toFixed(2)}`);
      } else {
        toast.success("Pedido creado");
      }
      onCreated();
    } catch { toast.error("Error creando pedido"); }
    setSubmitting(false);
  };

  const productsByCategory = categories.map(cat => ({
    ...cat, items: products.filter(p => p.category === cat.id || p.category_name === cat.name)
  }));

  const selectTable = (table) => {
    setDeliveryType("dineIn");
    setTableNumber(String(table.number));
    setTab("categorias");
    toast.success(`Mesa ${table.number} seleccionada`);
  };

  return (
    <div className="flex h-[calc(100vh-80px)]" data-testid="venta-directa">
      <div className="flex-1 flex flex-col border-r">
        <div className="p-4 border-b flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="venta-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <h1 className="text-xl font-bold uppercase">Venta directa</h1>
        </div>
        <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col">
          <div className="px-4 pt-2">
            <TabsList className="w-full">
              <TabsTrigger value="mesas" className="flex-1" data-testid="tab-mesas">MESAS</TabsTrigger>
              <TabsTrigger value="categorias" className="flex-1" data-testid="tab-categorias">CATEGORIAS</TabsTrigger>
              <TabsTrigger value="identificacion" className="flex-1" data-testid="tab-identificacion">IDENTIFICACION</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="mesas" className="flex-1 overflow-auto p-4">
            <div className="space-y-4">
              <Input placeholder="Buscar mesa..." className="bg-secondary border-transparent" data-testid="search-tables" />
              <div className="grid grid-cols-2 gap-3" data-testid="tables-grid">
                {tables.length > 0 ? tables.map(table => (
                  <div key={table.id || table.number} className={`p-4 rounded-lg cursor-pointer transition-all text-center ${table.status === "occupied" ? "bg-red-500 text-white hover:bg-red-600" : tableNumber === String(table.number) ? "bg-primary text-white" : "bg-green-500 text-white hover:bg-green-600"}`}
                    onClick={() => selectTable(table)} data-testid={`table-card-${table.number}`}>
                    <p className="font-bold text-lg uppercase">{table.name || `MESA ${String(table.number).padStart(2, "0")}`}</p>
                    <p className="text-sm opacity-80 flex items-center justify-center gap-1"><Clock className="w-3 h-3" />{new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</p>
                    <p className="text-sm">{table.status === "occupied" ? "Ocupada" : "Libre"}</p>
                  </div>
                )) : (
                  <>
                    {["BALCAO", "BALCAO 02", "CLIENTE NO LOCAL", ...Array.from({ length: 8 }, (_, i) => `MESA ${String(i + 1).padStart(2, "0")}`)].map((name, i) => (
                      <div key={i} className={`p-4 rounded-lg cursor-pointer transition-all text-center ${tableNumber === String(i + 1) ? "bg-primary text-white" : "bg-green-500 text-white hover:bg-green-600"}`}
                        onClick={() => selectTable({ number: i + 1, name })} data-testid={`table-card-${i + 1}`}>
                        <p className="font-bold text-lg uppercase">{name}</p>
                        <p className="text-sm opacity-80 flex items-center justify-center gap-1"><Clock className="w-3 h-3" />{new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}</p>
                        <p className="text-sm">Libre</p>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </TabsContent>
          <TabsContent value="categorias" className="flex-1 overflow-auto p-4">
            {loading ? <LoadingScreen /> : (
              <div className="space-y-6">
                <div className="flex gap-2 flex-wrap">
                  {["counter", "phone", "whatsapp", "table"].map(s => (
                    <Button key={s} variant={source === s ? "default" : "outline"} size="sm" onClick={() => setSource(s)} data-testid={`source-${s}`}>{sourceLabels[s]}</Button>
                  ))}
                </div>
                {productsByCategory.length > 0 ? productsByCategory.map(cat => (
                  <div key={cat.id || cat.name}>
                    <h3 className="font-bold uppercase text-sm mb-3 text-muted-foreground">{cat.name}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {cat.items.map(prod => {
                        const inCart = cart.find(c => c.product_id === prod.id);
                        return (
                          <div key={prod.id} className={`border rounded-lg p-3 cursor-pointer transition-all hover:shadow-sm ${inCart ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}
                            onClick={() => addToCart(prod)} data-testid={`product-${prod.id}`}>
                            {(prod.pos_media_url || prod.image_url) && <img src={(prod.pos_media_url || prod.image_url).startsWith("/api/") ? `${API_URL}${prod.pos_media_url || prod.image_url}` : (prod.pos_media_url || prod.image_url)} alt={prod.name} className="w-full h-20 object-cover rounded mb-2" />}
                            <p className="text-sm font-medium truncate">{prod.name}</p>
                            <p className="text-sm text-primary font-bold">{formatPrice(prod.price)}</p>
                            {inCart && <Badge className="mt-1 bg-primary text-white">{inCart.quantity}x</Badge>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8">
                    <ChefHat className="w-12 h-12 mx-auto text-muted-foreground mb-3 opacity-30" />
                    <p className="text-muted-foreground">Agrega productos desde el menu</p>
                  </div>
                )}
                <ManualProductEntry onAdd={(item) => setCart([...cart, item])} />
              </div>
            )}
          </TabsContent>
          <TabsContent value="identificacion" className="flex-1 overflow-auto p-4">
            <div className="max-w-md space-y-4">
              <div className="space-y-2">
                <Label>{locale.labels.phone} <span className="text-red-500">*</span></Label>
                <Input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder={locale.phonePrefix + "..."} className="bg-secondary border-transparent text-lg" data-testid="customer-phone" />
              </div>
              <div className="space-y-2">
                <Label>{locale.labels.name} <span className="text-red-500">*</span></Label>
                <Input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Nombre del cliente (obligatorio)"
                  className={`bg-secondary border-transparent ${!customerName.trim() ? "border-red-300 border" : ""}`} data-testid="customer-name" />
                {!customerName.trim() && <p className="text-xs text-red-500">El nombre del cliente es obligatorio</p>}
              </div>
              <Separator />
              <Label className="font-bold">Tipo de entrega</Label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: "delivery", label: locale.labels.delivery, icon: Truck },
                  { value: "pickup", label: locale.labels.pickup, icon: ShoppingBag },
                  { value: "dineIn", label: locale.labels.dineIn, icon: ChefHat }
                ].map(dt => (
                  <Button key={dt.value} variant={deliveryType === dt.value ? "default" : "outline"} className="flex-col h-auto py-3 gap-1"
                    onClick={() => setDeliveryType(dt.value)} data-testid={`delivery-type-${dt.value}`}>
                    <dt.icon className="w-5 h-5" />
                    <span className="text-xs">{dt.label}</span>
                  </Button>
                ))}
              </div>
              {deliveryType === "delivery" && (
                <div className="space-y-2">
                  <Label>{locale.labels.address}*</Label>
                  <AddressAutocomplete value={deliveryAddress} onChange={setDeliveryAddress}
                    onSelect={({ address, lat, lng }) => { setDeliveryAddress(address); setDeliveryLat(lat); setDeliveryLng(lng); }}
                    placeholder="Buscar direccion..." className="bg-secondary border-transparent" data-testid="delivery-address" />
                </div>
              )}
              {deliveryType === "dineIn" && (
                <div className="space-y-2">
                  <Label>Mesa</Label>
                  <Input type="number" value={tableNumber} onChange={e => setTableNumber(e.target.value)} placeholder="Numero de mesa" className="bg-secondary border-transparent" data-testid="table-number" />
                </div>
              )}
              <Separator />
              <div className="space-y-2">
                <Label>Forma de pago</Label>
                <Select value={paymentMethod} onValueChange={(v) => { setPaymentMethod(v); setNeedsChange(false); setCashReceived(""); }}>
                  <SelectTrigger className="bg-secondary border-transparent" data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {paymentMethods.map(m => <SelectItem key={m.name} value={m.name}>{m.name}</SelectItem>)}
                    {paymentMethods.length === 0 && (
                      <><SelectItem value="cash">Efectivo</SelectItem><SelectItem value="card">Tarjeta</SelectItem><SelectItem value="online">Online</SelectItem></>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Cash Change Section - Only for cash payments */}
              {(paymentMethod.toLowerCase().includes("efectivo") || paymentMethod.toLowerCase().includes("cash") || paymentMethod.toLowerCase().includes("dinheiro")) && (
                <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-amber-800">Precisa de troco?</Label>
                    <div className="flex gap-2">
                      <Button size="sm" variant={needsChange ? "default" : "outline"} onClick={() => setNeedsChange(true)} className={needsChange ? "bg-amber-600 hover:bg-amber-700" : ""}>Sim</Button>
                      <Button size="sm" variant={!needsChange ? "default" : "outline"} onClick={() => { setNeedsChange(false); setCashReceived(""); }} className={!needsChange ? "bg-gray-600 hover:bg-gray-700" : ""}>Não</Button>
                    </div>
                  </div>
                  
                  {needsChange && (
                    <div className="space-y-2">
                      <Label className="text-xs text-amber-700">Cliente vai pagar com:</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-amber-800">€</span>
                        <Input 
                          type="number" 
                          step="0.01"
                          value={cashReceived} 
                          onChange={e => setCashReceived(e.target.value)}
                          placeholder="0.00"
                          className="bg-white border-amber-300 text-lg font-bold text-center"
                          data-testid="cash-received-input"
                        />
                      </div>
                      
                      {/* Quick amounts */}
                      <div className="flex gap-1 flex-wrap">
                        {[5, 10, 20, 50].map(amt => (
                          <Button key={amt} type="button" size="sm" variant="outline" className="text-xs" onClick={() => setCashReceived(String(amt))}>
                            €{amt}
                          </Button>
                        ))}
                      </div>
                      
                      {cashReceived && parseFloat(cashReceived) >= total && (
                        <div className="mt-2 p-3 bg-green-100 rounded-lg border border-green-300">
                          <div className="flex justify-between text-sm">
                            <span className="text-green-700">Total do pedido:</span>
                            <span className="font-bold text-green-800">€{total.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-green-700">Valor recebido:</span>
                            <span className="font-bold text-green-800">€{parseFloat(cashReceived).toFixed(2)}</span>
                          </div>
                          <Separator className="my-2 bg-green-300" />
                          <div className="flex justify-between">
                            <span className="text-green-700 font-medium">TROCO:</span>
                            <span className="text-2xl font-black text-green-700">€{(parseFloat(cashReceived) - total).toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                      
                      {cashReceived && parseFloat(cashReceived) < total && (
                        <p className="text-xs text-red-600 font-medium">Valor insuficiente</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observaciones del pedido" className="bg-secondary border-transparent" data-testid="order-notes" />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="is-scheduled" checked={isScheduled} onChange={e => { setIsScheduled(e.target.checked); if (!e.target.checked) setScheduledFor(""); }} className="w-4 h-4 accent-primary" data-testid="scheduled-toggle" />
                  <Label htmlFor="is-scheduled" className="cursor-pointer flex items-center gap-1.5"><ChefHat className="w-3.5 h-3.5 text-blue-500" />Agendar pedido</Label>
                </div>
                {isScheduled && (
                  <Input type="datetime-local" value={scheduledFor} onChange={e => setScheduledFor(e.target.value)} className="bg-secondary border-blue-300 text-sm" data-testid="scheduled-for-input" />
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <div className="w-[340px] flex flex-col bg-card" data-testid="venta-cart">
        <div className="p-4 border-b">
          <h2 className="font-bold uppercase text-sm">{cart.length === 0 ? "Su pedido esta vacio" : `${cart.reduce((s, c) => s + c.quantity, 0)} item(s)`}</h2>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3">
            {cart.map(item => (
              <div key={item.product_id} className="space-y-1" data-testid={`cart-item-${item.product_id}`}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.product_name}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQty(item.product_id, -1)}>-</Button>
                    <span className="text-sm font-bold w-5 text-center">{item.quantity}</span>
                    <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQty(item.product_id, 1)}>+</Button>
                    <span className="text-sm font-medium w-14 text-right">{formatPrice(item.quantity * item.unit_price)}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => removeItem(item.product_id)} data-testid={`remove-item-${item.product_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                <Input
                  value={item.notes || ""}
                  onChange={e => updateNotes(item.product_id, e.target.value)}
                  placeholder="Obs. do item..."
                  className="h-7 text-xs bg-background border-muted pl-2"
                  data-testid={`item-notes-${item.product_id}`}
                />
              </div>
            ))}
          </div>
        </ScrollArea>
        <div className="p-4 border-t space-y-2">
          <div className="flex justify-between text-sm"><span>{locale.labels.subtotal}</span><span>{formatPrice(subtotal)}</span></div>
          {shipping > 0 && <div className="flex justify-between text-sm"><span>{locale.labels.shipping}</span><span>{formatPrice(shipping)}</span></div>}
          <Separator />
          <div className="flex justify-between font-bold text-lg"><span>{locale.labels.total}</span><span data-testid="cart-total">{formatPrice(total)}</span></div>
          <Button className="w-full glow-orange h-12 text-base" onClick={submit} disabled={submitting || cart.length === 0} data-testid="submit-order-btn">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : `Crear Pedido · ${formatPrice(total)}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
